import {useCallback, useEffect, useState} from 'react'
import {Button, Card, DatePicker, Input, Popconfirm, Select, Space, Table, Tag, Tooltip, Typography, message, type TableProps} from 'antd'
import {DeleteOutlined, EditOutlined, EyeOutlined, FileExcelOutlined, PlusOutlined} from '@ant-design/icons'
import {useNavigate} from 'react-router-dom'
import {api, getErrorMessage} from '../api/client'
import {useAuth} from '../auth/AuthContext'
import {emptyLookups, type Lookups} from '../components/CaseFilters'
import {StatusTag} from '../components/StatusTag'
import {TableFilterHeader} from '../components/TableFilterHeader'
import {PageTitle} from './DashboardPage'
import type {InvestigationCase} from '../types'

const statusOptions = ['NEW', 'ASSIGNED', 'IN_PROGRESS', 'PENDING', 'COMPLETED', 'CLOSED'].map(value => ({
  value,
  label: value.replace('_', ' '),
}))

const entityOptions = (items: {id: number; name: string}[]) => items.map(({id, name}) => ({value: id, label: name}))

export const CaseListPage = ({mine = false}: {mine?: boolean}) => {
  const [rows, setRows] = useState<InvestigationCase[]>([])
  const [lookups, setLookups] = useState<Lookups>(emptyLookups)
  const [loading, setLoading] = useState(true)
  const [filters, setFilters] = useState<Record<string, unknown>>({})
  const [pagination, setPagination] = useState({current: 1, pageSize: 20, total: 0})
  const [messageApi, contextHolder] = message.useMessage()
  const navigate = useNavigate()
  const {hasPermission} = useAuth()

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const response = await api.get(mine ? '/cases/mine' : '/cases', {
        params: mine ? filters : {...filters, page: pagination.current, pageSize: pagination.pageSize},
      })
      setRows(response.data.data)
      setPagination(current => ({
        ...current,
        total: response.data.pagination?.total ?? response.data.data.length,
      }))
    } catch (error) {
      messageApi.error(getErrorMessage(error))
    } finally {
      setLoading(false)
    }
  }, [filters, mine, pagination.current, pagination.pageSize])

  useEffect(() => {
    void api.get('/cases/lookups/options')
      .then(response => setLookups(response.data.data))
      .catch(error => messageApi.error(getErrorMessage(error)))
  }, [])

  useEffect(() => {
    const timer = window.setTimeout(() => void load(), 350)
    return () => window.clearTimeout(timer)
  }, [load])

  const setFilter = (key: string, value: unknown) => {
    setPagination(current => ({...current, current: 1}))
    setFilters(current => {
      const next = {...current}
      if (value === undefined || value === null || value === '') delete next[key]
      else next[key] = value
      return next
    })
  }

  const setDateRange = (dates: Parameters<NonNullable<React.ComponentProps<typeof DatePicker.RangePicker>['onChange']>>[0]) => {
    setPagination(current => ({...current, current: 1}))
    setFilters(current => {
      const next = {...current}
      if (dates?.[0]) next.dateFrom = dates[0].format('YYYY-MM-DD')
      else delete next.dateFrom
      if (dates?.[1]) next.dateTo = dates[1].format('YYYY-MM-DD')
      else delete next.dateTo
      return next
    })
  }

  const textFilter = (label: string, key: string) => (
    <TableFilterHeader label={label}>
      <Input size="small" allowClear placeholder="Filter" value={filters[key] as string | undefined} onChange={event => setFilter(key, event.target.value)} />
    </TableFilterHeader>
  )

  const selectFilter = (label: string, key: string, options: {value: string | number; label: string}[]) => (
    <TableFilterHeader label={label}>
      <Select size="small" allowClear showSearch optionFilterProp="label" value={filters[key] as string | number | undefined} options={options} onChange={value => setFilter(key, value)} />
    </TableFilterHeader>
  )

  const remove = async (id: number) => {
    try {
      await api.delete('/cases/' + id)
      messageApi.success('Case deleted')
      void load()
    } catch (error) {
      messageApi.error(getErrorMessage(error))
    }
  }

  const downloadExcel = async () => {
    try {
      const response = await api.get('/reports/excel', {params: filters, responseType: 'blob'})
      const url = URL.createObjectURL(response.data)
      const link = document.createElement('a')
      link.href = url
      link.download = 'eagleeye-cases.xlsx'
      link.click()
      URL.revokeObjectURL(url)
    } catch (error) {
      messageApi.error(getErrorMessage(error))
    }
  }

  const columns: TableProps<InvestigationCase>['columns'] = [
    {title: textFilter('Case No', 'caseNo'), dataIndex: 'caseNo', fixed: 'left', width: 145, sorter: !mine},
    {title: textFilter('Patient', 'patientName'), dataIndex: 'patientName', width: 165, sorter: !mine},
    {title: selectFilter('Insurance Agency', 'insuranceAgencyId', entityOptions(lookups.insuranceAgencies)), width: 185, render: (_, row) => row.insuranceAgency?.name},
    {title: selectFilter('Hospital', 'hospitalId', entityOptions(lookups.hospitals)), width: 175, render: (_, row) => row.hospital?.name},
    {title: selectFilter('Region', 'regionId', entityOptions(lookups.regions)), width: 145, render: (_, row) => row.region?.name},
    {title: selectFilter('Type', 'caseType', [{value: 'FULL', label: 'Full'}, {value: 'HALF', label: 'Half'}]), dataIndex: 'caseType', width: 125, sorter: !mine, render: value => <Tag>{value}</Tag>},
    {title: <TableFilterHeader label="Assigned Date"><DatePicker.RangePicker size="small" onChange={setDateRange} /></TableFilterHeader>, dataIndex: 'assignedDate', width: 235, sorter: !mine},
    {title: selectFilter('Hospital Staff', 'hospitalAssignedTo', entityOptions(lookups.staff)), width: 165, render: (_, row) => row.hospitalAssignee?.name ?? '—'},
    {title: selectFilter('Hospital Status', 'hospitalStatus', statusOptions), dataIndex: 'hospitalStatus', width: 165, sorter: !mine, render: value => <StatusTag status={value} />},
    {title: selectFilter('Insured Staff', 'insuredAssignedTo', entityOptions(lookups.staff)), width: 165, render: (_, row) => row.insuredAssignee?.name ?? '—'},
    {title: selectFilter('Insured Status', 'insuredStatus', statusOptions), dataIndex: 'insuredStatus', width: 165, sorter: !mine, render: value => <StatusTag status={value} />},
    {title: selectFilter('Overall Status', 'overallStatus', statusOptions), dataIndex: 'overallStatus', width: 165, sorter: !mine, render: value => <StatusTag status={value} />},
    {title: <TableFilterHeader label="Fees" />, dataIndex: 'fees', width: 90, sorter: !mine},
    {title: <TableFilterHeader label="TA" />, dataIndex: 'ta', width: 80, sorter: !mine},
    {title: <TableFilterHeader label="MRD" />, dataIndex: 'mrd', width: 80, sorter: !mine},
    {title: <TableFilterHeader label="Total" />, width: 95, render: (_, row) => Number(row.fees) + Number(row.ta) + Number(row.mrd)},
    {
      title: <TableFilterHeader label="Actions" />,
      fixed: 'right',
      width: 135,
      render: (_, row) => (
        <Space>
          <Tooltip title="View"><Button size="small" icon={<EyeOutlined />} onClick={() => navigate('/cases/' + row.id)} /></Tooltip>
          {hasPermission('cases.edit') && <Tooltip title="Edit"><Button size="small" icon={<EditOutlined />} onClick={() => navigate('/cases/' + row.id + '/edit')} /></Tooltip>}
          {hasPermission('cases.delete') && <Popconfirm title="Delete this case?" onConfirm={() => void remove(row.id)}><Button size="small" danger icon={<DeleteOutlined />} /></Popconfirm>}
        </Space>
      ),
    },
  ]

  const changeTable: TableProps<InvestigationCase>['onChange'] = (page, _tableFilters, sorter) => {
    setPagination(current => ({...current, current: page.current ?? 1, pageSize: page.pageSize ?? 20}))
    if (!Array.isArray(sorter) && sorter.field) {
      const field = Array.isArray(sorter.field) ? sorter.field[0] : sorter.field
      setFilters(current => ({...current, sortBy: field, sortOrder: sorter.order === 'ascend' ? 'ASC' : 'DESC'}))
    }
  }

  return (
    <>
      {contextHolder}
      <PageTitle
        title={mine ? 'My Assigned Cases' : 'Case Management'}
        subtitle={mine ? 'Cases where you own the hospital or insured investigation.' : 'Track assignments, progress, and case financials.'}
        actions={!mine && hasPermission('cases.add') ? <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/cases/new')}>Add Case</Button> : undefined}
      />
      <Card className="section-card">
        <div className="table-summary">
          <Typography.Text strong>{pagination.total.toLocaleString()} total cases</Typography.Text>
          {hasPermission('reports.view') && hasPermission('reports.excel') && <Button icon={<FileExcelOutlined />} onClick={() => void downloadExcel()}>Download Excel</Button>}
        </div>
        <Table
          rowKey="id"
          loading={loading}
          bordered
          dataSource={rows}
          columns={columns}
          scroll={{x: 2200}}
          pagination={{
            current: mine ? undefined : pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50, 100],
            showTotal: (total, range) => range[0] + '–' + range[1] + ' of ' + total,
          }}
          onChange={changeTable}
        />
      </Card>
    </>
  )
}
